import {
  SaveOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import {
  Form,
  Input,
  Button,
  Space,
  Typography,
  Skeleton,
  Tooltip,
  Upload,
  Spin,
  Flex,
} from 'antd';

import { Error403Unauthorized, ExtendedMessage } from '@app/components';
import {
  INSTAGRAM,
  MESSAGE,
  PREFIX,
  RULES_IS_REQUIRE,
  TITLE,
} from '@app/constants';
import { useManageMessageTemplate } from '@app/pages/Messages/ManageMessageTemplate/hooks/useManageMessageTemplate';
import { useManageMessageTemplateEffects } from '@app/pages/Messages/ManageMessageTemplate/hooks/useManageMessageTemplateEffects';
import '@pages/Messages/ManageMessageTemplate/styles/ManageMessageTemplate.desktop.scss';
import '@pages/Messages/ManageMessageTemplate/styles/ManageMessageTemplate.tablet.scss';

const { Text } = Typography;
const { Dragger } = Upload;

const TITLE_MIN_LENGTH = 3;

const pageClassName = `${PREFIX}manage-message-template`;

export const ManageMessageTemplate = () => {
  const {
    form,
    fieldContainerDynamicStyles,
    fieldWidth,
    contentWidth,
    isEditMode,
    templateId,
    hasUserRightToMessageTemplatesModule,
    sendFormDataToServer,
    cancelFormDataSaving,
    templateFiles,
    setTemplateFiles,
    handleRemoveExistingFile,
    newFiles,
    handleRemoveNewFileByIndex,
    isFilesSizeExceeded,
    isFilesMutationsLoading,
    uploadProps,
    filesHelpText,
  } = useManageMessageTemplate();

  const { isLoading, isFetching } = useManageMessageTemplateEffects({
    form,
    templateId,
    isEditMode,
    setTemplateFiles,
  });

  if (!hasUserRightToMessageTemplatesModule) {
    return <Error403Unauthorized />;
  }

  if (!contentWidth) {
    return <Skeleton className={`${pageClassName} skeleton`} active />;
  }

  return (
    <div className={pageClassName}>
      <Form className="form" form={form} onFinish={sendFormDataToServer}>
        <div className="field-container" style={fieldContainerDynamicStyles}>
          <Text className="label-require" data-testid="template-name-title">
            Название
          </Text>
          <Form.Item name={TITLE} rules={RULES_IS_REQUIRE}>
            <Input
              data-testid="template-name"
              className="title-field"
              maxLength={100}
              style={{ ...fieldWidth }}
            />
          </Form.Item>
        </div>

        <div className="field-container" style={fieldContainerDynamicStyles}>
          <Text className="label-require" data-testid="template-message-title">
            Текст сообщения
          </Text>
          <ExtendedMessage
            form={form}
            socialType={INSTAGRAM}
            formItemName={MESSAGE}
            formItemClassName={''}
            formItemRules={[
              ...RULES_IS_REQUIRE,
              {
                min: TITLE_MIN_LENGTH,
                message: `Минимум: ${TITLE_MIN_LENGTH} символа`,
              },
            ]}
            messageFieldStyles={{ ...fieldWidth }}
            textAreaDataTestId="template-message"
            previewButtonDataTestId="preview-message"
            emojiButtonDataTestId="emoji-icon"
            keywordsButtonDataTestId="template-icon"
          />
        </div>

        <div className="field-container" style={fieldContainerDynamicStyles}>
          <Text data-testid="template-files-title" strong>
            Файлы
          </Text>

          <div className="files-field" style={{ ...fieldWidth }}>
            <Form.Item
              className="files-upload"
              extra={
                <span
                  style={{
                    color: isFilesSizeExceeded ? '#ff4d4f' : 'rgba(0,0,0,0.45)',
                  }}
                >
                  {filesHelpText}
                </span>
              }
            >
              <Spin spinning={isFilesMutationsLoading}>
                <Dragger {...uploadProps}>
                  <Space direction="vertical">
                    <PaperClipOutlined />
                    <span>
                      Перетащите файлы сюда или нажмите, чтобы выбрать
                    </span>
                  </Space>
                </Dragger>
              </Spin>
            </Form.Item>
            <Spin spinning={isFilesMutationsLoading || isLoading || isFetching}>
              {(templateFiles.length > 0 || newFiles.length > 0) && (
                <Space
                  direction="vertical"
                  size={8}
                  className="files-list full-width"
                >
                  {templateFiles.map((file) => (
                    <Flex
                      key={file.Id}
                      className="files-list__item full-width"
                      align="center"
                      justify="space-between"
                      data-testid="template-file-item"
                    >
                      <Text className="files-list__name">{file.Name}</Text>
                      <Tooltip title="Удалить файл">
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveExistingFile(file.Id)}
                          disabled={isFilesMutationsLoading}
                        />
                      </Tooltip>
                    </Flex>
                  ))}

                  {newFiles.map((file, index) => (
                    <Flex
                      key={`${file.FileName}-${file.Size ?? 0}-${index}`}
                      className="files-list__item"
                      align="center"
                      justify="space-between"
                      data-testid="template-new-file-item"
                    >
                      <Text className="files-list__name">
                        {file.NewName || file.FileName}
                      </Text>
                      <Tooltip title="Удалить файл">
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveNewFileByIndex(index)}
                          disabled={isFilesMutationsLoading}
                        />
                      </Tooltip>
                    </Flex>
                  ))}
                </Space>
              )}
            </Spin>
          </div>
        </div>

        <Form.Item>
          <Space>
            <Button
              className="cancel-button"
              onClick={cancelFormDataSaving}
              data-testid="cancel"
            >
              Отменить
            </Button>

            <Button
              className="save-button"
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              data-testid="save"
            >
              Сохранить
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};
